import { useCallback, useEffect, useRef, useState } from 'react';
import Logo from '@fhnw/components/Logo/Logo';
import { Container } from '@plone/components';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import Navigation from '@plone/volto/components/theme/Navigation/Navigation';
import { Link } from '@fhnw/components/Link/Link';
import cx from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';
import MobileNavigation from './MobileNavigation';
import { useSelector, shallowEqual } from 'react-redux';
import { NavItem } from '@fhnw/types/navItem';
import { NavigationFooter } from './NavigationFooter';

interface HeaderProps {
  pathname: string;
}

const Header: React.FC<HeaderProps> = ({ pathname }) => {
  const firstLevelDivRef = useRef<HTMLDivElement>(null);
  const secondLevelDivRef = useRef<HTMLDivElement>(null);
  const [openMenuFirst, setOpenMenuFirst] = useState<boolean>(false);
  const [openMenuSecond, setOpenMenuSecond] = useState<boolean>(false);
  const [firstLevel, setFirstLevel] = useState<number>(-1);
  const [secondLevel, setSecondLevel] = useState<number>(-1);
  const [showFirstLevel, setShowFirstLevel] = useState<boolean>(false);
  const [showSecondLevel, setShowSecondLevel] = useState<boolean>(false);

  const firstLevelItemsRef = useRef<(HTMLAnchorElement | null)[]>([]);
  const secondLevelItemsRef = useRef<(HTMLAnchorElement | null)[]>([]);

  const token = useSelector(
    (state: any) => state.userSession.token,
    shallowEqual,
  );

  const contentData = useSelector(
    (state: any) => state.content?.data || state.fallbackLayout?.data,
  );

  const metaNav = contentData?.['@components']?.metanav || [];
  const navItems = contentData?.['@components']?.navtree?.items;

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLAnchorElement>,
    index: number,
    level: 'first' | 'second',
  ) => {
    if (event.key === 'Tab' && event.shiftKey) {
      const itemsRef =
        level === 'first' ? firstLevelItemsRef : secondLevelItemsRef;
      if (level === 'first' ? openMenuFirst : openMenuSecond) {
        event.preventDefault();
        itemsRef.current[index]?.focus();
      }
    }
  };

  const getMs = (s: string) =>
    s
      .split(',')
      .map((x) => parseFloat(x) * 1000)
      .reduce((a, b) => Math.max(a, b), 0);

  const getAnimDuration = useCallback((el: HTMLElement) => {
    const cs = getComputedStyle(el);
    return Math.max(getMs(cs.animationDuration), getMs(cs.transitionDuration));
  }, []);

  const closeTimeoutRefFirstLevel = useRef<number | null>(null);
  const closeTimeoutRefSecondLevel = useRef<number | null>(null);

  useEffect(() => {
    if (!openMenuFirst) {
      const el = firstLevelDivRef.current;
      const ms = el ? getAnimDuration(el) : 300;
      closeTimeoutRefFirstLevel.current = window.setTimeout(() => {
        setShowFirstLevel(false);
      }, ms);
    } else {
      setShowFirstLevel(true);
    }
    return () => {
      if (closeTimeoutRefFirstLevel.current) {
        clearTimeout(closeTimeoutRefFirstLevel.current);
        closeTimeoutRefFirstLevel.current = null;
      }
    };
  }, [openMenuFirst, getAnimDuration]);

  useEffect(() => {
    if (!openMenuSecond) {
      const el = secondLevelDivRef.current;
      const ms = el ? getAnimDuration(el) : 300;
      closeTimeoutRefSecondLevel.current = window.setTimeout(() => {
        setShowSecondLevel(false);
      }, ms);
    } else {
      setShowSecondLevel(true);
    }
    return () => {
      if (closeTimeoutRefSecondLevel.current) {
        clearTimeout(closeTimeoutRefSecondLevel.current);
        closeTimeoutRefSecondLevel.current = null;
      }
    };
  }, [openMenuSecond, getAnimDuration]);

  const handleNavigation = (
    level: 'first' | 'second',
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    index: number,
    remoteUrl: string,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    if (level === 'first') {
      if (!remoteUrl && (!openMenuFirst || firstLevel !== index)) {
        setFirstLevel(index);
        setOpenMenuFirst(true);
        setOpenMenuSecond(false);
        setSecondLevel(-1);
      } else {
        closeNavigation();
      }
    } else {
      if (!remoteUrl && !openMenuSecond) {
        setSecondLevel(index);
        setOpenMenuSecond(true);
      } else {
        setOpenMenuSecond(false);
        setSecondLevel(-1);
      }
    }
  };

  const closeNavigation = () => {
    setOpenMenuFirst(false);
    setOpenMenuSecond(false);
    setFirstLevel(-1);
    setSecondLevel(-1);
  };

  return (
    <>
      <header className="header-slot">
        <OutsideClickHandler onOutsideClick={closeNavigation} display="flex">
          <Container layout>
            <div className="upper-section">
              <Logo />
              <Navigation
                firstLevel={firstLevel}
                handleNavigation={handleNavigation}
                navItems={navItems}
                firstLevelItemsRef={firstLevelItemsRef}
              />
            </div>
            <NavigationFooter token={token} metaNav={metaNav} />
          </Container>

          {showFirstLevel && (
            <div
              ref={firstLevelDivRef}
              tabIndex={-1}
              className={cx('submenu-wrapper', {
                'slide-in-left': openMenuFirst,
                'slide-out-left': !openMenuFirst,
              })}
            >
              <h2>{navItems?.[firstLevel]?.title}</h2>
              <ul>
                {navItems?.[firstLevel]?.items.map(
                  (subitem: NavItem, index2: number) => (
                    <li key={subitem['@id']}>
                      <Link
                        href={subitem.remoteUrl ?? '#'}
                        className="btn btn-nav--sublevel"
                        data-is-expandable={!subitem.remoteUrl}
                        data-is-active={index2 === secondLevel}
                        ref={(el) => {
                          secondLevelItemsRef.current[index2] =
                            el as HTMLAnchorElement | null;
                        }}
                        onKeyDown={(
                          event: React.KeyboardEvent<HTMLAnchorElement>,
                        ) => handleKeyDown(event, index2, 'first')}
                        onClick={(
                          event: React.MouseEvent<
                            HTMLAnchorElement,
                            MouseEvent
                          >,
                        ) => {
                          !subitem.remoteUrl
                            ? handleNavigation(
                                'second',
                                event,
                                index2,
                                subitem.remoteUrl,
                              )
                            : closeNavigation();
                        }}
                        linkComponent={UniversalLink}
                      >
                        {subitem.title}
                      </Link>
                    </li>
                  ),
                )}
              </ul>
            </div>
          )}

          {showSecondLevel && (
            <div
              ref={secondLevelDivRef}
              tabIndex={-1}
              className={cx('subsubmenu-wrapper', {
                'slide-in-left': openMenuSecond,
                'slide-out-left': !openMenuSecond,
              })}
            >
              <h2>{navItems?.[firstLevel]?.items?.[secondLevel]?.title}</h2>
              <ul>
                {navItems?.[firstLevel]?.items?.[secondLevel]?.items.map(
                  (subsubitem: NavItem, index3: number) => (
                    <li key={subsubitem['@id']}>
                      <Link
                        href={subsubitem.remoteUrl ?? '#'}
                        className="btn btn-nav--sublevel"
                        data-is-expandable={false}
                        onKeyDown={(
                          event: React.KeyboardEvent<HTMLAnchorElement>,
                        ) => handleKeyDown(event, index3, 'second')}
                        onClick={closeNavigation}
                        linkComponent={UniversalLink}
                      >
                        {subsubitem.title}
                      </Link>
                    </li>
                  ),
                )}
              </ul>
            </div>
          )}

          <MobileNavigation
            navItems={navItems}
            token={token}
            metaNav={metaNav}
          />
        </OutsideClickHandler>
      </header>
    </>
  );
};

export default Header;
